import React, { useRef } from 'react';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { EqualisationFormValues } from './FormWrapper';
import EqualisationRealisationAllocationsForm from './EqualisationRealisationAllocationsForm';
import TabsPanel from 'further-ui/components/TabsPanel';
import EqualisationAllocationsForm from './EqualisationAllocationsForm';
import Divider from '@mui/material/Divider';
import { styled, Typography } from '@mui/material';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { Column } from 'further-ui/components/Layout';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import AvatarWithName from 'components/new/AvatarWithName';
import { numberToCurrencyString } from 'further-ui/utils';

const ContentColumn = styled(Column)(({ theme }) => ({
  padding: theme.spacing(
    0,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
  ),
}));

interface ActionCardProps {
  action: EqualisationFormValues['actions'][number] & {
    originalRealisationAllocations?: EqualisationFormValues['actions'][number]['linkedActions'][number]['originalRealisationAllocations'];
    newRealisationAllocations?: EqualisationFormValues['actions'][number]['linkedActions'][number]['newRealisationAllocations'];
  };
  actionIndex: number;
  linkedActionIndex?: number;
  baseCurrency: CurrencyCode;
}

const ActionCard: React.FC<ActionCardProps> = ({
  action,
  actionIndex,
  baseCurrency,
  linkedActionIndex,
}) => {
  const { expandedRows, toggleRowExpansion } = useExpandedRows();
  const frozenOriginalAllocations = useRef(action.originalAllocations);

  const linkedActions = action.linkedActions.map((linkedAction) => ({
    id: linkedAction.actionId,
    ...linkedAction,
  }));

  const linkedActionColumns: Array<
    TableColumn<(typeof linkedActions)[number]>
  > = [
    {
      key: 'investor',
      label: 'Investor',
      render: (row) => <AvatarWithName name={row.counterpartyLabel} />,
    },
    {
      key: 'date',
      label: 'Date',
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (row) =>
        numberToCurrencyString(row.totalAmountLcy, { currency: baseCurrency }),
    },
  ];

  return (
    <>
      <Divider />
      <ContentColumn spacing="lg">
        <TabsPanel
          tabs={[
            {
              label: 'New allocation',
              key: 'newAllocation',
              content:
                action.newAllocations.length > 0 ? (
                  <EqualisationAllocationsForm
                    fields={action.newAllocations}
                    inputAmountLcy={action.totalAmountLcy}
                    isNotEditable={false}
                    actionIndex={actionIndex}
                    allocationRule={action.allocationRule}
                    allocationType="newAllocations"
                    linkedActionIndex={linkedActionIndex}
                    transactionCurrency={baseCurrency}
                  />
                ) : (
                  <EqualisationRealisationAllocationsForm
                    transactionCurrency={baseCurrency}
                    isNotEditable={false}
                    actionIndex={actionIndex}
                    allocationType="newRealisationAllocations"
                    allocationRule={action.allocationRule}
                    linkedActionIndex={linkedActionIndex}
                  />
                ),
            },
            {
              label: 'Original allocation',
              key: 'originalAllocation',
              content:
                frozenOriginalAllocations.current &&
                frozenOriginalAllocations.current.length > 0 ? (
                  <EqualisationAllocationsForm
                    fields={frozenOriginalAllocations.current}
                    inputAmountLcy={action.totalAmountLcy}
                    isNotEditable={true}
                    actionIndex={actionIndex}
                    allocationRule={action.allocationRule}
                    allocationType="originalAllocations"
                    linkedActionIndex={linkedActionIndex}
                    transactionCurrency={baseCurrency}
                  />
                ) : (
                  <EqualisationRealisationAllocationsForm
                    transactionCurrency={baseCurrency}
                    allocationRule={action.allocationRule}
                    isNotEditable={true}
                    actionIndex={actionIndex}
                    allocationType="originalRealisationAllocations"
                    linkedActionIndex={linkedActionIndex}
                  />
                ),
            },
          ]}
        />
        {linkedActions.length > 0 ? (
          <Column spacing="sm">
            <Typography variant="h6" color="grey.600">
              Linked actions
            </Typography>
            <Table
              showTopBorder
              showHeaderBorder
              isExpandedRow={(row) => expandedRows.has(row.id)}
              onRowClick={(row) => toggleRowExpansion(row.id)}
              onRowExpand={(row) => toggleRowExpansion(row.id)}
              isExpandableRow={() => true}
              getExpandedContent={(row, _width, rowIndex) => (
                <ActionCard
                  key={rowIndex}
                  action={{
                    ...row,
                    linkedActions: [],
                    actionType: 'Linked action',
                  }}
                  actionIndex={actionIndex}
                  linkedActionIndex={rowIndex ?? 0}
                  baseCurrency={baseCurrency}
                />
              )}
              rows={linkedActions}
              columns={linkedActionColumns}
            />
          </Column>
        ) : null}
      </ContentColumn>
    </>
  );
};

export default ActionCard;
